import { ROOM_CODE_LENGTH } from '@pixelworld/shared';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Field } from '../../components/Field';
import { SkyPage } from '../../components/SkyPage';
import { clientGames } from '../../game-host/registry';
import { da } from '../../i18n/da';
import { useAuth } from '../auth/auth-context';
import { BlockPanel } from '../admin/BlockPanel';
import { ChatLogPanel } from '../admin/ChatLogPanel';
import { ReportsPanel } from '../admin/ReportsPanel';
import { StatusPanel } from '../admin/StatusPanel';
import { FriendsPanel } from '../friends/FriendsPanel';
import { PublicRoomList } from './PublicRoomList';
import { parseRoomCode } from './room-code';
import styles from './StartPage.module.css';

export function StartPage() {
  const navigate = useNavigate();
  const { state, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState(() => searchParams.get('room') ?? '');
  const [privateRoom, setPrivateRoom] = useState(false);
  const game = clientGames[0];
  const user = state.status === 'ready' ? state.user : null;

  const validCode = parseRoomCode(code);

  if (!game || !user) {
    return null;
  }

  const play = (query: string) => {
    void navigate(`/play/${game.id}?${query}`);
  };
  const joinRoom = (roomCode: string) => play(`room=${roomCode}`);

  const handleJoin = (event: FormEvent) => {
    event.preventDefault();
    if (validCode) joinRoom(validCode);
  };

  return (
    <SkyPage>
      <div className={styles.account}>
        <span>
          {da.start.hello} <strong data-testid="signed-in-as">{user.displayName}</strong>
        </span>
        <Button size="small" variant="quiet" onClick={() => void navigate('/profile')}>
          ✎ {da.profile.edit}
        </Button>
        <Button size="small" variant="quiet" onClick={() => void signOut()}>
          {da.auth.logout}
        </Button>
      </div>

      <Card aria-labelledby="game-title">
        <div className={styles.banner}>
          <h2 id="game-title" className={styles.gameTitle}>
            {game.title}
          </h2>
          <p className={styles.gameTagline}>{game.tagline}</p>
          <span className={styles.badge}>{da.start.players(game.minPlayers, game.maxPlayers)}</span>
        </div>

        <div className={styles.form}>
          <Button onClick={() => play('mode=quick')}>{da.start.playNow}</Button>

          <FriendsPanel />

          <PublicRoomList gameId={game.id} onEnter={joinRoom} />

          <div className={styles.divider}>
            <span>{da.start.or}</span>
          </div>

          <div className={styles.createRow}>
            <Button
              variant="secondary"
              onClick={() => play(privateRoom ? 'mode=private' : 'mode=public')}
            >
              {da.start.createRoom}
            </Button>
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={privateRoom}
                onChange={(event) => setPrivateRoom(event.target.checked)}
              />
              {da.start.privateRoom}
            </label>
          </div>

          <div className={styles.divider}>
            <span>{da.start.or}</span>
          </div>

          <form className={styles.joinRow} onSubmit={handleJoin}>
            <Field
              id="room-code"
              label={da.start.codeLabel}
              inputClassName={styles.codeInput}
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              placeholder={da.start.codePlaceholder}
              maxLength={ROOM_CODE_LENGTH + 2}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
            />
            <Button type="submit" variant="secondary" disabled={!validCode}>
              {da.start.joinRoom}
            </Button>
          </form>
        </div>

        {user.role === 'admin' && (
          <>
            <StatusPanel />
            <BlockPanel />
            <ReportsPanel />
            <ChatLogPanel />
          </>
        )}
        <p className={styles.parents}>
          <Link to="/parents">{da.parents.link}</Link>
        </p>
      </Card>
    </SkyPage>
  );
}
